import { Chip, Typography } from '@mui/material';
import moment from 'moment';
import {
	AutocompleteInput,
	Datagrid,
	DateField,
	Filter,
	FunctionField,
	ReferenceField,
	ReferenceInput,
	TextField,
	useListContext,
	usePermissions,
} from 'react-admin';
import { BetweenDatesTimesInput } from '../../components/BetweenDatesInput';
import Permission from '../../components/Permission';
import ReportList from '../../components/ReportList';
import RequiredChain from '../../components/RequiredChain';
import { PERMISSIONS } from '../../constants';
import { getChainId } from '../../lib';
import {
	ROLETA_REPORT_RESOURCE, ROLETA_RESOURCE, RoletaPrizeStatus, RoletaSegmentKind, RoletaSpinStatus,
} from '../../models/roleta';
import { filterToQuery } from '../../utils';

const titleSx = {
	margin: '10px 0 10px 0',
	textAlign: 'center',
	fontWeight: 'bold',
};

const formatBRL = (value: any): string =>
	Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const formatNumber = (value: any): string => Number(value || 0).toLocaleString('pt-BR');

const formatPct = (value: any): string =>
	value === undefined || value === null
		? '-'
		: `${(Number(value) * 100).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;

type ChipColor = 'default' | 'error' | 'info' | 'success' | 'warning';

/** Situação do giro em si. */
const spinStatusChips: Record<string, { label: string; color: ChipColor }> = {
	[RoletaSpinStatus.Available]: { label: 'Giro pendente', color: 'warning' },
	[RoletaSpinStatus.Spinning]: { label: 'Girando', color: 'info' },
	[RoletaSpinStatus.Spun]: { label: 'Giro realizado', color: 'info' },
	[RoletaSpinStatus.Expired]: { label: 'Giro expirado', color: 'default' },
};

/** Situação da entrega do prêmio, quando a fatia sorteada era de prêmio. */
const prizeStatusChips: Record<string, { label: string; color: ChipColor }> = {
	[RoletaPrizeStatus.Pending]: { label: 'Prêmio sendo liberado', color: 'warning' },
	[RoletaPrizeStatus.Awarding]: { label: 'Prêmio sendo liberado', color: 'warning' },
	[RoletaPrizeStatus.Delivered]: { label: 'Prêmio entregue', color: 'success' },
	[RoletaPrizeStatus.Failed]: { label: 'Falha na entrega', color: 'error' },
};

const translateSpinStatus = (status?: string): string =>
	spinStatusChips[status || '']?.label || '-';

const translatePrizeStatus = (status?: string | null): string =>
	status ? (prizeStatusChips[status]?.label || '-') : '-';

const translateSegmentKind = (kind?: string | null): string =>
	kind ? (RoletaSegmentKind.translateKind(kind as RoletaSegmentKind) || '-') : '-';

const SpinStatusChip = ({ record }: { record: any }) => {
	const chip = spinStatusChips[record?.status || ''] || { label: '-', color: 'default' as ChipColor };
	return <Chip label={chip.label} size="small" color={chip.color} variant="outlined" />;
};

const PrizeStatusChip = ({ record }: { record: any }) => {
	if (!record?.prizeStatus) return <>-</>;
	const chip = prizeStatusChips[record.prizeStatus] || { label: '-', color: 'default' as ChipColor };
	return <Chip label={chip.label} size="small" color={chip.color} variant="outlined" />;
};

const summaryRows: { key: string; label: string; format?: (value: any) => string }[] = [
	{ key: 'spinsGranted', label: 'Giros concedidos' },
	{ key: 'spinsUsed', label: 'Giros realizados' },
	{ key: 'spinsPending', label: 'Giros pendentes' },
	{ key: 'spinsExpired', label: 'Giros expirados' },
	{ key: 'prizesDelivered', label: 'Prêmios entregues' },
	{ key: 'prizesPending', label: 'Prêmios sendo liberados' },
	{ key: 'nothingCount', label: 'Passou a vez' },
	{ key: 'extraSpins', label: 'Gire de novo' },
	{ key: 'uniqueCustomers', label: 'Clientes únicos' },
	{ key: 'totalCost', label: 'Custo total (R$)', format: formatBRL },
	{ key: 'costPerSpin', label: 'Custo por giro (R$)', format: formatBRL },
];

const SummaryTable = ({ totals }: { totals: any }) => {
	if (!totals) return null;

	const rows = summaryRows
		.filter(({ key }) => totals[key] !== undefined && totals[key] !== null)
		.map(({ key, label, format }) => ({
			id: key,
			label,
			value: format ? format(totals[key]) : formatNumber(totals[key]),
		}));

	if (rows.length === 0) return null;

	return (
		<Datagrid bulkActionButtons={false} data={rows} total={rows.length} sx={{ mb: 4 }}>
			<TextField source="label" label="Métrica" sortable={false} />
			<TextField source="value" label="Valor" textAlign="center" sortable={false} />
		</Datagrid>
	);
};

const RoletaReportList = () => {
	const { data, filterValues } = useListContext();

	if (!filterValues?.roletaId) {
		return <Typography align="center" sx={{ py: 4 }}>Selecione uma roleta para visualizar o relatório.</Typography>;
	}

	let rows: any[] = [];
	let summaryData: any = null;

	if (data) {
		const info = data[0];
		if (info) {
			rows = info.data || [];
			summaryData = info.summaryData;
		}
	}

	const bySegment: any[] = summaryData?.bySegment || [];

	return (
		<>
			<Typography sx={titleSx}>Resumo</Typography>
			<SummaryTable totals={summaryData?.totals} />

			<Typography sx={titleSx}>Por fatia</Typography>
			<Datagrid bulkActionButtons={false} data={bySegment} total={bySegment.length} sx={{ mb: 4 }}>
				<TextField source="label" label="Fatia" sortable={false} />
				<FunctionField
					label="Tipo"
					sortable={false}
					render={(record: any) => translateSegmentKind(record.kind)}
				/>
				<FunctionField
					label="Peso"
					textAlign="center"
					sortable={false}
					render={(record: any) => formatNumber(record.weight)}
				/>
				<FunctionField
					label="% configurado"
					textAlign="center"
					sortable={false}
					render={(record: any) => formatPct(record.configuredPct)}
				/>
				<FunctionField
					label="% real"
					textAlign="center"
					sortable={false}
					render={(record: any) => formatPct(record.realPct)}
				/>
				<FunctionField
					label="Vezes sorteada"
					textAlign="center"
					sortable={false}
					render={(record: any) => formatNumber(record.timesDrawn)}
				/>
				<FunctionField
					label="Estoque restante"
					textAlign="center"
					sortable={false}
					render={(record: any) => (
						record.remainingWins === null || record.remainingWins === undefined
							? 'Ilimitado'
							: formatNumber(record.remainingWins)
					)}
				/>
				<FunctionField
					label="Custo"
					textAlign="center"
					sortable={false}
					render={(record: any) => formatBRL(record.cost)}
				/>
			</Datagrid>

			<Typography sx={titleSx}>Giros</Typography>
			<Datagrid bulkActionButtons={false} data={rows} rowClick={false}>
				<ReferenceField
					label="Cliente"
					source="customerId"
					reference="customers"
					link="show"
					sortable={false}
				>
					<TextField source="name" />
				</ReferenceField>
				<FunctionField
					label="Situação"
					sortable={false}
					render={(record: any) => <SpinStatusChip record={record} />}
				/>
				<TextField source="segmentLabel" label="Fatia" sortable={false} emptyText="-" />
				<FunctionField
					label="Tipo da fatia"
					sortable={false}
					render={(record: any) => translateSegmentKind(record.segmentKind)}
				/>
				<TextField source="prizeName" label="Prêmio" sortable={false} emptyText="-" />
				<FunctionField
					label="Entrega"
					sortable={false}
					render={(record: any) => <PrizeStatusChip record={record} />}
				/>
				<DateField
					source="createdAt"
					label="Ganhou o giro"
					showTime
					locales="pt-BR"
					sortable={false}
				/>
				<DateField
					source="spunAt"
					label="Girou"
					showTime
					locales="pt-BR"
					sortable={false}
					emptyText="-"
				/>
			</Datagrid>
		</>
	);
};

const FilterTablePrint = () => {
	const { filterValues } = useListContext();
	const { permissions } = usePermissions();
	return (
		<Datagrid bulkActionButtons={false} data={[{ ...filterValues, generatedReportAt: moment().toISOString() }]} total={1}>
			<DateField source="from" textAlign="center" label="De" sortable={false} showTime locales="pt-BR" />
			<DateField source="to" textAlign="center" label="Até" sortable={false} showTime locales="pt-BR" />
			{
				permissions && permissions.includes(PERMISSIONS.ADMIN) &&
				<ReferenceField label="Rede" emptyText="Todas" source="chainId" textAlign="center" reference="chains" link={false} sortable={false}>
					<TextField source="name" />
				</ReferenceField>
			}
			<ReferenceField label="Roleta" emptyText="-" source="roletaId" reference={ROLETA_RESOURCE} link={false} sortable={false}>
				<TextField source="name" />
			</ReferenceField>
			<DateField source="generatedReportAt" textAlign="center" label="Gerado às" showTime sortable={false} locales="pt-BR" />
		</Datagrid>
	);
};

const ListFilters = (props: any) => {
	const { filterValues } = useListContext();
	return (
		<Filter {...props}>
			<BetweenDatesTimesInput format="YYYY-MM-DD HH:mm" alwaysOn />
			<Permission permission={PERMISSIONS.ADMIN} alwaysOn>
				<ReferenceInput
					reference="chains"
					source="chainId"
					sort={{ field: 'name', order: 'ASC' }}
				>
					<AutocompleteInput
						label="Rede"
						optionText="name"
						filterToQuery={filterToQuery}
					/>
				</ReferenceInput>
			</Permission>
			<ReferenceInput
				source="roletaId"
				reference={ROLETA_RESOURCE}
				filter={{ chainId: filterValues?.chainId || getChainId() }}
				sort={{ field: 'startAt', order: 'DESC' }}
				alwaysOn
			>
				<AutocompleteInput
					label="Roleta"
					optionText="name"
					filterToQuery={filterToQuery}
				/>
			</ReferenceInput>
		</Filter>
	);
};

const roletaExporter = async (data: any[], fetchRelatedRecords: any) => {
	const spins = data[0]?.data || [];

	const relations = [
		{ field: 'customerId', resource: 'customers' },
	];

	const fetchData = await fetchRelatedRecords(relations, spins);
	const getRelated = (resource: string) =>
		fetchData.find(({ resource: res }: any) => res === resource)?.results || [];

	return spins.map((item: any) => {
		const customer = getRelated('customers').find(({ id }: any) => id === item.customerId);
		return {
			cliente: customer?.name || 'Não identificado',
			situacao: translateSpinStatus(item.status),
			fatia: item.segmentLabel || '-',
			tipo_da_fatia: translateSegmentKind(item.segmentKind),
			premio: item.prizeName || '-',
			entrega: translatePrizeStatus(item.prizeStatus),
			data_do_giro_concedido: item.createdAt,
			data_do_giro_realizado: item.spunAt || '-',
		};
	});
};

const ReportRoleta = (props: any) => (
	<Permission permission={PERMISSIONS.GIFT}>
		<RequiredChain>
			<ReportList periodRequired
				{...props}
				basePath="roletas"
				title="Roleta da Sorte"
				resource={ROLETA_REPORT_RESOURCE}
				filters={<ListFilters />}
				sort={{ field: 'createdAt', order: 'DESC' }}
				filterDefaultValues={{
					from: moment().startOf('month').toISOString(),
					to: moment().endOf('day').toISOString(),
				}}
				customExporter={roletaExporter}
				bulkActionButtons={false}
				titleOnPrint="Relatório da Roleta da Sorte"
				filterTablePrint={<FilterTablePrint />}
				landscape
			>
				<RoletaReportList />
			</ReportList>
		</RequiredChain>
	</Permission>
);

export default ReportRoleta;
